import { useMemo, useRef, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { getApiErrorMessage } from "@/api";
import { AlertDialog } from "@/components/dialog/alert/AlertDialog";
import { Icon } from "@/components/icons/Icon";
import { MousePositionContextMenu } from "@/components/sidebar/context-menu/MousePositionContextMenu";
import { chatIconName } from "@/models/chat/chatIcons";
import { useTeamChats } from "@/store/useTeamChats";
import { twMerge } from "@/utils/twMerge";

import { ContextMenu } from "../documents/ContextMenu";
import { EmptyFolderText } from "../documents/EmptyFolderText";
import { ListItemTitle } from "../documents/ListItemTitle";
import { SectionDivider } from "../SectionDivider";
import { SidebarSectionHeader } from "../SidebarSectionHeader";
import { useRowFocusWithin } from "../useRowFocusWithin";
import { NavSection, TreeItem } from "../useSidebarNav";

import type { ChatShort } from "@/api";
import type { DropdownItem } from "@/components/sidebar/Dropdown";

const TrashedChatItem = observer(function TrashedChatItem({ chat }: { chat: ChatShort }) {
  const t = useTranslations();
  const teamChats = useTeamChats();
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [restoreAlertIsOpen, setRestoreAlertIsOpen] = useState(false);
  const [restoreError, setRestoreError] = useState("");
  const rowRef = useRef<HTMLDivElement>(null);
  const { focusWithin, focusWithinProps } = useRowFocusWithin();

  const menuItems = useMemo(
    () =>
      [
        {
          title: t("documentsSidebar.restoreChat"),
          icon: <Icon name="History" />,
          onClick: () => {
            teamChats.restoreChat(chat.uid).catch((err) => {
              setRestoreAlertIsOpen(true);
              setRestoreError(getApiErrorMessage(err));
            });
          },
        },
      ] satisfies DropdownItem[],
    [t, chat, teamChats],
  );

  return (
    <li role="none">
      <MousePositionContextMenu items={menuItems} onOpenChange={setContextMenuOpen}>
        <TreeItem
          ref={rowRef}
          focused={focusWithin}
          {...focusWithinProps}
          aria-label={chat.title || t("chat.untitledChat")}
          className={twMerge(
            "relative flex group rounded-6",
            "hover:bg-sidebar-button-hover-inverted-bg",
            "[&:has(button[data-open])]:bg-sidebar-button-hover-inverted-bg",
            contextMenuOpen && "bg-sidebar-button-hover-inverted-bg",
          )}
        >
          <div className="flex items-center gap-1.5 w-full min-h-[34px] pl-1 pr-8 sm:pr-14">
            <Icon name={`chat-icons/${chatIconName(chat)}`} className="shrink-0" />
            <ListItemTitle type="chat" className="py-0" title={chat.title || ""} />
          </div>
          <ContextMenu
            items={menuItems}
            title={chat.title || t("chat.untitledChat")}
            rowRef={rowRef}
          />
        </TreeItem>
      </MousePositionContextMenu>
      <AlertDialog
        isOpen={restoreAlertIsOpen}
        title={t("documentsSidebar.restoreChatError")}
        description={restoreError}
        onClose={() => {
          setRestoreAlertIsOpen(false);
          setRestoreError("");
        }}
      />
    </li>
  );
});

export const ChatsTrash = observer(function ChatsTrash() {
  const t = useTranslations();
  const teamChats = useTeamChats();
  const { isTrashOpen, trashedChats, trashStatus } = teamChats;

  return (
    <NavSection
      className="mt-1.25 p-1"
      data-testid="chats-trash"
      aria-label={t("documentsSidebar.deletedChats")}
      expanded={isTrashOpen}
      onToggle={() => teamChats.toggleTrash()}
    >
      <SidebarSectionHeader
        compact
        isOpen={isTrashOpen}
        onToggle={() => teamChats.toggleTrash()}
        title={t("documentsSidebar.deletedChats")}
      />
      {isTrashOpen && (
        <>
          <SectionDivider className="mt-1 mb-[3px]" />
          {trashStatus === "error" && (
            <EmptyFolderText>{t("documentsSidebar.chatsError")}</EmptyFolderText>
          )}
          {trashStatus === "loaded" && trashedChats.length === 0 && (
            <EmptyFolderText>{t("documentsSidebar.noDeletedChats")}</EmptyFolderText>
          )}
          {trashedChats.length > 0 && (
            <ul role="group">
              {trashedChats.map((chat) => (
                <TrashedChatItem key={chat.uid} chat={chat} />
              ))}
            </ul>
          )}
        </>
      )}
    </NavSection>
  );
});
